import React, { useState } from "react";
import omit from "lodash/fp/omit";
import isEmpty from "lodash/fp/isEmpty";
import classNames from "classnames";
import SortDirection, {
  type SortDirectionType,
} from "@rio-cloud/rio-uikit/SortDirection";
import SortArrows from "@rio-cloud/rio-uikit/SortArrows";
import NotFoundState from "@rio-cloud/rio-uikit/NotFoundState";
import { formatMilisecondsInDaysHoursMinutes } from "../../../utils/dateUtils";
import { useMediaQuery } from "@mui/material";

const columnLabels: { [key: string]: string } = {
  group: "Grupo",
  und: "Unidade",
  qtd: "Qtde. Chamados",
  averageTime: "Tempo Médio",
};

const defaultColumnOrder = ["group", "und", "qtd"];

interface IGroupTable {
  metric: "average" | "quantity";
  filteredOccurrenceIdList: string[];
  allOccurrenceTimeSpentList: { occurrenceId: string, timeSpent: number, chassis: string }[];
  data: {
    group: string;
    items: {
      name: string;
      qtd: number;
      averageTime?: number;
      chassisList: string[];
      occurrenceIds: string[];
    }[];
    total: number;
    averageTime: number;
  }[];
}

const GroupTable = (props: IGroupTable) => {
  const [columnOrder, setColumnOrder] = useState<string[]>(defaultColumnOrder);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [sortByColumn, setSortByColumn] = useState<string>("group");
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(
    SortDirection.ASCENDING
  );
  const isResponsiveView = useMediaQuery("(max-width: 1024px)");

  const handleSortChange = (event: React.MouseEvent<HTMLElement>) => {
    const column = event.currentTarget.getAttribute("data-sortby") || "";
    if (sortByColumn === column) {
      setSortDirection(
        sortDirection === SortDirection.ASCENDING
          ? SortDirection.DESCENDING
          : SortDirection.ASCENDING
      );
    } else {
      setSortByColumn(column);
      setSortDirection(SortDirection.ASCENDING);
    }
  };

  const getSortedData = () => {
    if (!sortByColumn || !props.data) return props.data;

    const sortedData = [...props.data].sort((a, b) => {
      let aValue, bValue;

      if (sortByColumn === 'group') {
        aValue = a.group?.toLowerCase() || '';
        bValue = b.group?.toLowerCase() || '';

        if (sortDirection === SortDirection.ASCENDING) {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }

      if (sortByColumn === 'qtd') {
        aValue = a.total || 0;
        bValue = b.total || 0;

        if (sortDirection === SortDirection.ASCENDING) {
          return aValue - bValue;
        } else {
          return bValue - aValue;
        }
      }

      return 0;
    });

    return sortedData;
  };

  const tableHeadClassNames = classNames(
    "user-select-none",
    "sort-column",
    { "text-md py-3 px-2 font-bold": isResponsiveView }
  );
  const columns = columnOrder.filter((col) => !hiddenColumns.includes(col));
  const rows = omit(hiddenColumns, props.data);

  return (
    <div id="group-table" className={isResponsiveView ? "overflow-x-auto w-full" : ""}>
      <table className={`table table-layout-fixed table-column-overflow-hidden table-striped table-head-filled ${isResponsiveView ? 'text-md w-full' : ''}`}>
        <colgroup>
          <col style={{ width: isResponsiveView ? "65%" : "60%" }} />
          <col style={{ width: isResponsiveView ? "35%" : "40%" }} />
        </colgroup>
        <thead>
          <tr>
            {[
              { column: "group", label: columnLabels.group },
              { column: "qtd", label: props.metric === "average" ? columnLabels.averageTime : columnLabels.qtd },
            ].map(({ column, label }) => (
              <th
                key={column}
                className={tableHeadClassNames}
                onClick={handleSortChange}
                data-field={column}
                data-sortby={column}
                title={label}
              >
                <span className="flex items-center gap-[6px]">
                  {sortByColumn === column ? (
                    <SortArrows direction={sortDirection} />
                  ) : (
                    <SortArrows />
                  )}
                  <span className={`my-th ${isResponsiveView ? 'text-md font-bold' : ''}`}>{label}</span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isEmpty(rows) ? (
            <tr>
              <td colSpan={columns.length + 1}>
                <NotFoundState
                  headline="Nenhum dado encontrado"
                  message="Busque novamente"
                />
              </td>
            </tr>
          ) : (
            getSortedData()
              .filter((row) => row.items.length > 0 && row.group !== 'Total' && row.items.some(item => props.filteredOccurrenceIdList.some(id => item.occurrenceIds.includes(id))))
              .map((row, index) => {
                const itemsFiltered = row.items
                  .map(item => item.occurrenceIds)
                  .flat()
                  .filter(item => props.filteredOccurrenceIdList.includes(item))

                const averageTimeSpentByItems = row.items
                  .filter(item => itemsFiltered.some(id => item.occurrenceIds.includes(id)))
                  .map(item => {
                    const childItemsFiltered = item.occurrenceIds.filter(id => itemsFiltered.includes(id));
                    const chassisMap = new Map<string, number>();
                    childItemsFiltered.forEach(id => {
                      const occ = props.allOccurrenceTimeSpentList.find(el => el.occurrenceId === id);
                      if (occ) chassisMap.set(occ.chassis, (chassisMap.get(occ.chassis) ?? 0) + occ.timeSpent);
                    });
                    const chassisValues = Array.from(chassisMap.values());
                    return chassisValues.length > 0 ? chassisValues.reduce((acc, t) => acc + t, 0) / chassisValues.length : 0;
                  });

                return <React.Fragment key={`group-${index}`}>
                  <tr className="bg-[#F6F6F6] font-semibold">
                    <td className={isResponsiveView ? "text-md py-3 px-2" : ""}>{row.group}</td>
                    <td className={isResponsiveView ? "text-md py-3 px-2 text-right" : ""}>{props.metric === "average" ? formatMilisecondsInDaysHoursMinutes(((averageTimeSpentByItems ?? []).reduce((acc, timeSpent) => acc + timeSpent, 0) / (averageTimeSpentByItems ?? []).length) * 1000) : itemsFiltered.length}</td>
                  </tr>
                  {row.items
                    .filter(item => itemsFiltered.some(id => item.occurrenceIds.includes(id)))
                    .map((item, itemIndex) => {
                      const childItemsFiltered = item.occurrenceIds.filter(id => itemsFiltered.includes(id));
                      return (
                        <tr key={`item-${itemIndex}`}>
                          <td className={isResponsiveView ? "text-md py-3 px-2" : ""}>{item.name}</td>
                          <td className={isResponsiveView ? "text-md py-3 px-2 text-right" : ""}>
                            {props.metric === "average"
                              ? formatMilisecondsInDaysHoursMinutes((averageTimeSpentByItems[itemIndex] ?? 0) * 1000)
                              : childItemsFiltered.length}
                          </td>
                        </tr>
                      );
                    })}
                </React.Fragment>
              })
          )}

          {/* {props.data.length ? (
            <tr>
              <td className={isResponsiveView ? "text-md py-3 px-2 font-bold" : ""}>
                <strong>
                  {props.metric === "average" ? "Média Total" : "Total"}
                </strong>
              </td>
              <td className={isResponsiveView ? "text-md py-3 px-2 text-right font-bold" : ""}>
                <strong>
                  {props.metric === "average"
                    ? props.data.find((row) => row.group === 'Total')?.averageTime
                    : props.data.find((row) => row.group === 'Total')?.total}
                </strong>
              </td>
            </tr>
          ) : null} */}
        </tbody>
      </table>
    </div>
  );
};

export default GroupTable;
